import { useCallback, useEffect, useState } from 'react';

import { useRevalidator } from '@remix-run/react';

import useGlobalModals from '~/components/GlobalModals/hooks/useGlobalModals';
import type { ModalType } from '~/interfaces/modal_type';

export interface UpgradeHandoff<T> {
  /** What the open modal is waiting on, or null once access landed or nothing was bought. */
  awaiting: T | null;
  markAwaitingAccess: (awaiting: T) => void;
  onAccessGranted: () => void;
}

interface UseUpgradeHandoffParams<T> {
  /** The modal whose URL param has to drop before the layout is allowed to revalidate. */
  modalType: ModalType;
  /** Must change identity with the loader data it reads, or the handoff never re-checks it. */
  isGranted: (awaiting: T) => boolean;
  /** Runs once the loader reports access. */
  onGranted: (awaiting: T) => void;
}

/**
 * Post-purchase handoff shared by the upgrade modals. Revalidation is suppressed while a
 * modal param is in the URL, so the order is fixed: close -> revalidate -> `onGranted`.
 * Mount it in the layout, since the sequence outlives the modal that started it.
 */
export const useUpgradeHandoff = <T>({
  modalType,
  isGranted,
  onGranted,
}: UseUpgradeHandoffParams<T>): UpgradeHandoff<T> => {
  const { currentModal, handleCloseModal } = useGlobalModals();
  const revalidator = useRevalidator();

  const [awaiting, setAwaiting] = useState<T | null>(null);
  const [purchased, setPurchased] = useState<T | null>(null);
  const [handoff, setHandoff] = useState<'idle' | 'closing' | 'revalidating'>('idle');

  // closing -> revalidating: the URL dropped the modal param, so revalidation is no longer suppressed.
  useEffect(() => {
    if (handoff !== 'closing' || currentModal === modalType) return;
    revalidator.revalidate();
    setHandoff('revalidating');
  }, [handoff, currentModal, modalType, revalidator]);

  // revalidating -> idle: the loader now reports access, so hand the purchase to the epilogue.
  useEffect(() => {
    if (handoff !== 'revalidating' || purchased === null || !isGranted(purchased)) return;
    setHandoff('idle');
    setPurchased(null);
    onGranted(purchased);
  }, [handoff, purchased, isGranted, onGranted]);

  const markAwaitingAccess = useCallback((next: T) => {
    setPurchased(next);
    setAwaiting(next);
  }, []);

  // idle -> closing: close the modal so the URL drops its param.
  const onAccessGranted = useCallback(() => {
    setAwaiting(null);
    setHandoff('closing');
    handleCloseModal({ preventScrollReset: true });
  }, [handleCloseModal]);

  return { awaiting, markAwaitingAccess, onAccessGranted };
};
